import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Check, X, User, Calendar, Shield, Search } from 'lucide-react';
import api from '../utils/api';

const PlanManagement = () => {
    const [plans, setPlans] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [activeTab, setActiveTab] = useState('standard'); // 'standard' or 'custom'
    const [editingId, setEditingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        maxUsers: '',
        features: '',
        durationDays: 30,
        tenantId: '' // For custom plans
    });

    useEffect(() => {
        fetchPlans();
        fetchTenants();
    }, []);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const response = await api('/master-admin/plans');
            if (response.ok) {
                const data = await response.json();
                setPlans(data);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTenants = async () => {
        try {
            const response = await api('/master-admin/super-admins');
            if (response.ok) {
                const data = await response.json();
                setTenants(data);
            }
        } catch (error) {
            console.error('Error fetching tenants:', error);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (activeTab === 'custom' && !formData.tenantId) {
            alert('Please select a user to assign the custom plan to.');
            return;
        }

        const planData = {
            ...formData,
            features: formData.features.split(',').map(f => f.trim()).filter(f => f !== '')
        };

        try {
            let url, method;

            if (activeTab === 'custom') {
                // Custom plans: always use the dedicated endpoint (creates + assigns immediately)
                if (!editingId) {
                    url = '/master-admin/create-custom-plan';
                    method = 'POST';
                } else {
                    url = `/master-admin/plans/${editingId}`;
                    method = 'PUT';
                }
            } else {
                // Standard plans
                url = editingId ? `/master-admin/plans/${editingId}` : '/master-admin/plans';
                method = editingId ? 'PUT' : 'POST';
            }

            const response = await api(url, {
                method,
                body: planData
            });

            if (response.ok) {
                alert(activeTab === 'custom' ? 'Custom plan created and assigned successfully!' : 'Plan saved successfully!');
                fetchPlans();
                resetForm();
            } else {
                const err = await response.json();
                alert(err.message || 'Error saving plan');
            }
        } catch (error) {
            console.error('Error saving plan:', error);
        }
    };

    const handleEdit = (plan) => {
        setEditingId(plan._id);
        setFormData({
            name: plan.name,
            price: plan.price,
            maxUsers: plan.maxUsers,
            features: plan.features.join(', '),
            durationDays: plan.durationDays,
            tenantId: plan.assignedToTenant || ''
        });
        setActiveTab(plan.isCustom ? 'custom' : 'standard');
        setIsAdding(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this plan?')) return;
        try {
            const response = await api(`/master-admin/plans/${id}`, { method: 'DELETE' });
            if (response.ok) {
                fetchPlans();
            }
        } catch (error) {
            console.error('Error deleting plan:', error);
        }
    };

    const resetForm = () => {
        setIsAdding(false);
        setEditingId(null);
        setFormData({
            name: '',
            price: '',
            maxUsers: '',
            features: '',
            durationDays: 30,
            tenantId: ''
        });
    };

    const filteredPlans = plans.filter(p => activeTab === 'custom' ? p.isCustom : !p.isCustom);
    
    const filteredTenants = tenants.filter(t => 
        t.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        t.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.companyName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: '850', color: '#0f172a', letterSpacing: '-0.02em' }}>Plan Management</h1>
                    <p style={{ color: '#64748b', fontSize: '15px', marginTop: '4px' }}>Configure standard pricing tiers or create exclusive custom plans for specific users.</p>
                </div>
                {!isAdding && (
                    <button 
                        onClick={() => setIsAdding(true)}
                        style={btnPrimaryLargeStyle}
                    >
                        <Plus size={20} /> {activeTab === 'standard' ? 'New Standard Plan' : 'Create Custom Plan'}
                    </button>
                )}
            </div>

            {/* Tabs */}
            {!isAdding && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: '#f1f5f9', padding: '6px', borderRadius: '12px', width: 'fit-content' }}>
                    <button 
                        onClick={() => setActiveTab('standard')}
                        style={activeTab === 'standard' ? tabActiveStyle : tabInactiveStyle}
                    >
                        Standard Plans
                    </button>
                    <button 
                        onClick={() => setActiveTab('custom')}
                        style={activeTab === 'custom' ? tabActiveStyle : tabInactiveStyle}
                    >
                        Custom Plans
                    </button>
                </div>
            )}

            {isAdding && (
                <div style={formContainerStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>
                            {editingId ? 'Edit Plan' : activeTab === 'custom' ? 'Create Exclusive Custom Plan' : 'New Plan Details'}
                        </h3>
                        <button onClick={resetForm} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={24} /></button>
                    </div>
                    
                    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                        {activeTab === 'custom' && !editingId && (
                            <div style={{ ...inputGroupStyle, gridColumn: 'span 2' }}>
                                <label style={labelStyle}>Select User / Tenant</label>
                                <div style={{ position: 'relative' }}>
                                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                    <input 
                                        placeholder="Search by name, email or company..." 
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        style={{ ...inputStyle, paddingLeft: '40px', marginBottom: '8px' }}
                                    />
                                </div>
                                <select 
                                    name="tenantId" 
                                    value={formData.tenantId} 
                                    onChange={handleInputChange} 
                                    style={inputStyle}
                                    required
                                >
                                    <option value="">-- Choose a Super Admin --</option>
                                    {filteredTenants.map(t => (
                                        <option key={t._id} value={t.tenantId}>
                                            {t.name} ({t.companyName}) - {t.email}
                                        </option>
                                    ))}
                                </select>
                                <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>The custom plan will be immediately assigned to this user upon creation.</p>
                            </div>
                        )}

                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>Plan Name</label>
                            <input name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. Starter, Pro, Enterprise" style={inputStyle} required />
                        </div>
                        {/* Hide price for custom plans — custom plans are always manually assigned */}
                        {activeTab !== 'custom' && (
                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>Price (₹ INR)</label>
                            <input type="number" name="price" value={formData.price} onChange={handleInputChange} placeholder="e.g. 2999" style={inputStyle} required />
                        </div>
                        )}
                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>Max User Seats (Employees)</label>
                            <input type="number" name="maxUsers" value={formData.maxUsers} onChange={handleInputChange} placeholder="Total employees allowed" style={inputStyle} required />
                        </div>
                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>Duration (Days)</label>
                            <input type="number" name="durationDays" value={formData.durationDays} onChange={handleInputChange} style={inputStyle} required />
                        </div>
                        <div style={{ ...inputGroupStyle, gridColumn: 'span 2' }}>
                            <label style={labelStyle}>Features (Comma separated)</label>
                            <textarea name="features" value={formData.features} onChange={handleInputChange} placeholder="Feature 1, Feature 2, Feature 3" style={{ ...inputStyle, height: '100px', resize: 'none' }} required />
                        </div>
                        <div style={{ display: 'flex', gap: '12px', gridColumn: 'span 2', marginTop: '12px' }}>
                            <button type="submit" style={btnPrimaryStyle}>
                                {editingId ? 'Update Plan' : activeTab === 'custom' ? 'Create & Assign Plan' : 'Create Plan'}
                            </button>
                            <button type="button" onClick={resetForm} style={btnSecondaryStyle}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {!isAdding && (
                <>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '80px', color: '#64748b' }}>
                            <div className="spinner" style={{ marginBottom: '16px' }}></div>
                            Loading plans...
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
                            {filteredPlans.map(plan => (
                                <div key={plan._id} style={planCardStyle}>
                                    {plan.isCustom && (
                                        <div style={customBadgeStyle}>Exclusive Custom Plan</div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                                        <div>
                                            <h4 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>{plan.name}</h4>
                                            <div style={{ fontSize: '32px', fontWeight: '900', color: '#2563eb', margin: '4px 0' }}>
                                                ₹{plan.price}<span style={{ fontSize: '14px', fontWeight: '400', color: '#64748b' }}>/{plan.durationDays}d</span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={() => handleEdit(plan)} style={iconBtnStyle} title="Edit"><Edit2 size={18} /></button>
                                            <button onClick={() => handleDelete(plan._id)} style={{ ...iconBtnStyle, color: '#ef4444' }} title="Delete"><Trash2 size={18} /></button>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                                        <div style={statBoxStyle}>
                                            <User size={14} color="#64748b" />
                                            <span style={{ fontWeight: '700' }}>{plan.maxUsers}</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>Seats</span>
                                        </div>
                                        <div style={statBoxStyle}>
                                            <Calendar size={14} color="#64748b" />
                                            <span style={{ fontWeight: '700' }}>{plan.durationDays}</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>Days</span>
                                        </div>
                                    </div>

                                    {plan.isCustom && plan.assignedToTenant && (
                                        <div style={{ marginBottom: '20px', padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Assigned To</div>
                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                                                Tenant ID: {plan.assignedToTenant}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Shield size={14} /> Features Included
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {plan.features.map((feature, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: '#475569' }}>
                                                    <Check size={18} color="#22c55e" style={{ flexShrink: 0 }} /> {feature}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {filteredPlans.length === 0 && (
                                <div style={{ gridColumn: 'span 3', textAlign: 'center', padding: '80px', background: 'white', borderRadius: '24px', border: '2px dashed #e2e8f0' }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>{activeTab === 'custom' ? '💎' : '📦'}</div>
                                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>No {activeTab} plans found</h3>
                                    <p style={{ color: '#64748b', marginTop: '8px' }}>Click the button above to create your first {activeTab} plan.</p>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const tabActiveStyle = { 
    padding: '8px 20px', background: 'white', color: '#2563eb', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontSize: '14px' 
};
const tabInactiveStyle = { 
    padding: '8px 20px', background: 'transparent', color: '#64748b', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' 
};
const formContainerStyle = { 
    background: 'white', padding: '32px', borderRadius: '24px', border: '1px solid #e2e8f0', marginBottom: '32px', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' 
};
const planCardStyle = { 
    background: 'white', borderRadius: '24px', border: '1px solid #e2e8f0', padding: '28px', position: 'relative', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', transition: 'all 0.3s ease'
};
const customBadgeStyle = {
    position: 'absolute', top: '-12px', left: '24px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', boxShadow: '0 4px 12px rgba(217,119,6,0.3)'
};
const statBoxStyle = {
    flex: 1, padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px'
};
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: '8px' };
const labelStyle = { fontSize: '14px', fontWeight: '700', color: '#334155' };
const inputStyle = { padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '15px', width: '100%', outline: 'none', transition: 'border-color 0.2s' };
const btnPrimaryStyle = { padding: '12px 28px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontSize: '15px' };
const btnPrimaryLargeStyle = { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 10px 15px -3px rgba(37,99,235,0.4)' };
const btnSecondaryStyle = { padding: '12px 28px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontSize: '15px' };
const iconBtnStyle = { padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '10px', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s' };

export default PlanManagement;
